import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { JwtAuthConstants } from '../constants/jwt-auth.constants';
import { SessionStorageService } from '../services/session-storage.service';
import { API_ENDPOINT, SESSION_STORAGE_KEY } from '../configs';
import { ROUTE_PATH } from '../constants';
import { HttpClient } from '@angular/common/http';
import { AuthStateService } from '@core/services/auth-state.service';

@Injectable()
export class JWTInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private sessionStorageService: SessionStorageService,
    private authStateService: AuthStateService,
    private http: HttpClient,
    private router: Router,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip adding token for refresh token request to avoid infinite loop
    if (request.url.includes('refresh-token')) {
      return next.handle(request);
    }

    const accessToken = this.authStateService.accessToken ?? "";
    const userId = this.authStateService.userId ?? "";

    if (accessToken && userId) {
      request = this.addTokenToRequest(request, accessToken, userId);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Check if error is due to TOKEN_EXPIRED
        if (error.status === 401 && error.error?.code === JwtAuthConstants.TOKEN_EXPIRED) {
          // handle token refresh
          return this.handle401Error(request, next, userId);
        }

        // If token is invalid, redirect to login
        if (error.status === 401 && error.error?.code === JwtAuthConstants.TOKEN_INVALID) {
          this.logout();
          return throwError(() => error);
        }

        return throwError(() => error);
      })
    );
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string, userId: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'UserId': userId
      },
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, userId: string): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.refreshToken);

      if (refreshToken) {
        return this.refreshToken().pipe(
          switchMap((response: any) => {
            this.isRefreshing = false;

            // Check if refresh token is expired
            if (response?.message === JwtAuthConstants.REFRESH_TOKEN_EXPIRED || !response?.isSuccess) {
              this.logout();
              return throwError(() => new Error('Refresh token expired. Please login again.'));
            }

            if (response?.accessToken) {
              const newAccessToken = response.accessToken;
              
              // Save new access token to session storage
              this.authStateService.updateAccessToken(newAccessToken);
              this.refreshTokenSubject.next(newAccessToken);

              // Retry the failed request with new token
              return next.handle(this.addTokenToRequest(request, newAccessToken, userId));
            }

            // If refresh token failed, logout
            this.logout();
            return throwError(() => new Error('Refresh token failed'));
          }),
          catchError((error) => {
            this.isRefreshing = false;
            this.logout();
            return throwError(() => error);
          })
        );
      } else {
        this.isRefreshing = false;
        this.logout();
        return throwError(() => new Error('No refresh token available'));
      }
    }

    // If already refreshing, wait for the new token
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenToRequest(request, token!, userId)))
    );
  }

  private refreshToken(): Observable<any> {
    const accessToken = this.authStateService.accessToken;
    const userId = this.authStateService.userId;

    const refreshToken = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.refreshToken);
    
    return this.http.post(API_ENDPOINT.AUTH.REFRESH_TOKEN, {
      accessToken: accessToken,
      refreshToken: refreshToken,
      userId: userId,
    });
  }

  private logout(): void {
    this.sessionStorageService.clearSessionStorage();
    this.router.navigate([ROUTE_PATH.LOGIN]);
  }
}
